<template>
  <div id="app">
    <!-- Navigation Bar -->
    <NavBar 
      :isLoggedIn="isLoggedIn"
      :user="user"
      :searchQuery="searchQuery"
      @update-search="searchQuery = $event"
      @perform-search="performSearch"
      @show-login="showLoginModal = true"
      @logout="logout"
    />

    <!-- Main Content -->
    <main class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <transition name="fade" mode="out-in">
        <router-view 
          v-slot="{ Component }"
          :key="$route.fullPath"
        >
          <component 
            :is="Component"
            :articles="articles"
            :featuredJournals="featuredJournals"
            :recentArticles="recentArticles"
            :searchQuery="searchQuery"
            :user="user"
            :isLoggedIn="isLoggedIn"
            @view-article="viewArticle"
            @view-journal="viewJournal"
            @submit-article="submitArticle"
          />
        </router-view>
      </transition>
    </main>

    <!-- Login Modal -->
    <LoginModal 
      :show="showLoginModal"
      @close="showLoginModal = false"
      @login="handleLogin"
      @google-login="handleGoogleLogin"
      @microsoft-login="handleMicrosoftLogin"

      />

    <!-- User Profile Modal -->
    <UserProfileModal 
      :show="showProfileModal"
      :user="user"
      :role="invitedRole"
      @close="showProfileModal = false"
      @profile-completed="showProfileModal = false"
    />

    <!-- Footer -->
    <FooterComponent />
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRouter,useRoute } from 'vue-router'
import { initializeApp } from 'firebase/app'
import { useHead } from '@vueuse/head'
import {
  getFirestore, collection, getDocs, addDoc, doc, getDoc, updateDoc,query, where, limit 
} from 'firebase/firestore'
import {
  getAuth, signInWithEmailAndPassword, signInWithPopup,
  GoogleAuthProvider, signOut, onAuthStateChanged, OAuthProvider 
} from 'firebase/auth'

import NavBar from '@/components/NavBar.vue'
import LoginModal from '@/components/LoginModal.vue'
import FooterComponent from '@/components/FooterComponent.vue'
import UserProfileModal from '@/components/UserProfileModal.vue'
useHead({
  title: 'Daluyang Dunong Multidisciplinary Research Journal (DDMRJ) | Mindoro State University',
  meta: [
    { name: 'description', content: 'Daluyang Dunong (DDMRJ) is the internationally peer-reviewed, open-access, multidisciplinary journal of Mindoro State University.' },
    { name: 'keywords', content: 'Daluyang Dunong, DDMRJ, Mindoro State University, multidisciplinary journal, peer-reviewed, open access, Philippines research' },

    // Open Graph
    { property: 'og:title', content: 'Daluyang Dunong Multidisciplinary Research Journal (DDMRJ)' },
    { property: 'og:description', content: 'An internationally peer-reviewed, open-access multidisciplinary journal by Mindoro State University (MinSU).' },
    { property: 'og:type', content: 'website' },
    { property: 'og:url', content: 'https://yourdomain.com/journal' },
    { property: 'og:image', content: 'https://yourdomain.com/preview.jpg' },

    // Twitter Card
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: 'Daluyang Dunong Multidisciplinary Research Journal (DDMRJ)' },
    { name: 'twitter:description', content: 'Peer-reviewed, open-access multidisciplinary journal of Mindoro State University.' },
    { name: 'twitter:image', content: 'https://yourdomain.com/preview.jpg' }
  ]
});
const firebaseConfig = {
  apiKey: "AIzaSyDf3aeft1F1a8o5K80cwRA23exk98-D7EA",
  authDomain: "minsu-journ.firebaseapp.com",
  projectId: "minsu-journ",
  storageBucket: "minsu-journ.firebasestorage.app",
  messagingSenderId: "491091434927",
  appId: "1:491091434927:web:6758d9f1a17f892451235a",
  measurementId: "G-MZCXTCRHN3"
}

const app = initializeApp(firebaseConfig)
const db = getFirestore(app)
const auth = getAuth(app)
const googleProvider = new GoogleAuthProvider()

export default {

  name: 'App',
  components: {
    NavBar,
    LoginModal,
    FooterComponent,
    UserProfileModal
  },
  setup() {
    const router = useRouter()
    const route = useRoute()
    const invitedRole = ref('') 
    const showLoginModal = ref(false)
    const isLoggedIn = ref(false)
    const searchQuery = ref('')
    const articles = ref([])
    const featuredJournals = ref([])
    const currentArticle = ref(null)
    const showProfileModal = ref(false)

    const user = ref({
      name: '',
      email: '',
      avatar: '',
      uid: '',
      role: ''
    })

    const recentArticles = computed(() => {
      return [...articles.value]
        .filter(article => article.status === 'published')
        .sort((a, b) => new Date(b.publishedDate) - new Date(a.publishedDate))
        .slice(0, 5)
    })

    const performSearch = () => {
      if (searchQuery.value.trim()) {
        router.push('/browse')
      }
    }

    const viewArticle = (id) => {
      const found = articles.value.find(article => article.slug === id)
      if (found) {
        currentArticle.value = found
        router.push(`/article/${slug}`)
      } else {
        alert('Article not found.')
        router.push('/browse')
      }
    }

    const viewJournal = (id) => {
      console.log(`Viewing journal with ID: ${id}`)
    }

    const handleLogin = async (credentials) => {
      try {
        const userCredential = await signInWithEmailAndPassword(auth, credentials.email, credentials.password)
        const firebaseUser = userCredential.user

        const userRef = doc(db, 'users', firebaseUser.uid)
        const userSnap = await getDoc(userRef)
        const userData = userSnap.exists() ? userSnap.data() : {}

        user.value = {
          name: userData.name || firebaseUser.displayName || firebaseUser.email,
          email: firebaseUser.email,
          avatar: firebaseUser.photoURL || `https://ui-avatars.com/api/?name=${firebaseUser.email}&background=6366f1&color=fff`,
          uid: firebaseUser.uid,
          role: userData.role
        }

        isLoggedIn.value = true
        showLoginModal.value = false
      } catch (error) {
        console.error('Login error:', error)
        alert('Login failed: ' + error.message)
      }
    }
const handleMicrosoftLogin = async () => {
  try {
    const microsoftProvider = new OAuthProvider('microsoft.com');

    // Optional: force re-consent & pick account
    microsoftProvider.setCustomParameters({
      prompt: "consent",
      tenant: "common" // can be tenant ID if you want org-specific login
    });

    const result = await signInWithPopup(auth, microsoftProvider);
    const firebaseUser = result.user;

    // Check if user exists in Firestore
    const userRef = doc(db, 'users', firebaseUser.uid);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      await setDoc(userRef, {
        name: firebaseUser.displayName,
        email: firebaseUser.email,
        avatar: firebaseUser.photoURL || `https://ui-avatars.com/api/?name=${firebaseUser.email}&background=6366f1&color=fff`,
        role: 'researcher',
        createdAt: new Date()
      });
    }

    user.value = {
      name: firebaseUser.displayName,
      email: firebaseUser.email,
      avatar: firebaseUser.photoURL || `https://ui-avatars.com/api/?name=${firebaseUser.email}&background=6366f1&color=fff`,
      uid: firebaseUser.uid,
      role: userSnap.exists() ? userSnap.data().role : 'researcher'
    };

    isLoggedIn.value = true;
    showLoginModal.value = false;

  } catch (error) {
    console.error('Microsoft login error:', error);
    alert('Microsoft login failed: ' + error.message);
  }
};

  

    const handleGoogleLogin = async () => {
      try {
        const result = await signInWithPopup(auth, googleProvider)
        const firebaseUser = result.user

        user.value = {
          name: firebaseUser.displayName,
          email: firebaseUser.email,
          avatar: firebaseUser.photoURL,
          uid: firebaseUser.uid,
          role: 'researcher'
        }

        isLoggedIn.value = true
        showLoginModal.value = false
      } catch (error) {
        console.error('Google login error:', error)
        alert('Google login failed: ' + error.message)
      }
    }

    const logout = async () => {
      try {
        await signOut(auth)
        isLoggedIn.value = false
        user.value = { name: '', email: '', avatar: '', uid: '', role: '' }
      } catch (error) {
        console.error('Logout error:', error)
      }
    }
const submitArticle = async (articleData) => {
  try {
    if (articleData.id) {
      // 🔁 Edit existing article
      const { id, ...dataToUpdate } = articleData
      await updateDoc(doc(db, 'articles', id), {
        ...dataToUpdate,
        updatedAt: new Date()
      })
      alert('Article updated successfully!')
      console.log('✅ Updated article with ID:', id)
    } else {
      // 🆕 Submit new article
      const docRef = await addDoc(collection(db, 'articles'), {
        ...articleData,
        authorId: user.value.uid,
        publishedDate: new Date().toISOString(),
        views: 0,
        citations: 0,
        createdAt: new Date()
      })
      alert('Article submitted successfully!')
      console.log('🆕 Submitted new article with ID:', docRef.id)
    }

    await loadArticles()
  } catch (error) {
    console.error('❌ Error submitting article:', error)
    alert('Error submitting article: ' + error.message)
  }
}


    const loadArticles = async () => {
  try {
    const publishedQuery = query(
      collection(db, 'articles'),
      where('status', '==', 'published')
    )

    const querySnapshot = await getDocs(publishedQuery)
    const loadedArticles = []
    querySnapshot.forEach((doc) => {
      loadedArticles.push({ id: doc.id, ...doc.data() })
    })
    articles.value = loadedArticles
  } catch (error) {
    console.error('Error loading articles:', error)
    alert('Error loading articles: ' + error.message)
  }
}

   const loadFeaturedJournals = async () => {
  try {
    // query only featured journals, limit to 3
    const q = query(
      collection(db, 'articles'),
      where('featured', '==', true),
      limit(3)
    )

    const querySnapshot = await getDocs(q)
    const journals = []
    querySnapshot.forEach((doc) => {
      journals.push({ id: doc.id, ...doc.data() })
    })

    featuredJournals.value = journals
  } catch (error) {
    console.error('Error loading journals:', error)
    featuredJournals.value = []
  }
}

    
onMounted(async () => {
  // ✅ Check if current route is /accept-invite/:id
  if (route.path.startsWith('/accept-invite/')) {
    const inviteId = route.params.id
    console.log('Invite ID:', inviteId) // ✅ Log it here
   try {
  const inviteRef = doc(db, 'invitations', inviteId)
  const inviteSnap = await getDoc(inviteRef)

  if (inviteSnap.exists()) {
    const inviteData = inviteSnap.data()

    invitedRole.value = inviteData.role 
    showProfileModal.value = true

    console.log('[INVITE] Role from invitation:', inviteData.role)
  } else {
    console.warn(`[INVITE] No invitation found with ID: ${inviteId}`)
  }
} catch (error) {
  console.error('[INVITE] Error loading invitation:', error)
}

  }

  // 🔁 Auth observer
  onAuthStateChanged(auth, async (firebaseUser) => {
    if (firebaseUser) {
      const userRef = doc(db, 'users', firebaseUser.uid)
      const userSnap = await getDoc(userRef)

      const userData = userSnap.exists() ? userSnap.data() : {}

      user.value = {
        name: userData.name || firebaseUser.displayName || '',
        email: firebaseUser.email,
        avatar: firebaseUser.photoURL || `https://ui-avatars.com/api/?name=${firebaseUser.email}&background=6366f1&color=fff`,
        uid: firebaseUser.uid,
        role: userData.role || invitedRole.value
      }

      if (!userData.name || !userData.institution || !userData.fieldOfStudies) {
        showProfileModal.value = true
      }

      isLoggedIn.value = true
    } else {
      isLoggedIn.value = false
      user.value = { name: '', email: '', avatar: '', uid: '', role: '' }
    }
  })

  await loadArticles()
  await loadFeaturedJournals()
})

    return {
  showLoginModal,
  isLoggedIn,
  searchQuery,
  user,
  articles,
  featuredJournals,
  currentArticle,
  recentArticles,
  performSearch,
  viewArticle,
  viewJournal,
  handleLogin,
  handleGoogleLogin,
  handleMicrosoftLogin,
  logout,
  submitArticle,
  showProfileModal,
  invitedRole // ← ✅ Add this line
}

  }
}
</script>

<style>
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}
.article-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1);
}
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: #f1f1f1;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 3px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #555;
}
.filter-section {
  transition: all 0.3s ease;
}
.filter-section.collapsed {
  max-height: 0;
  overflow: hidden;
  opacity: 0;
}
.filter-section.expanded {
  max-height: 500px;
  opacity: 1;
}
</style>
